import { useCallback, useEffect, useState } from 'react';
import { getBackground, type BackgroundReport } from '../../api/admin';
import { ApiError } from '../../api/client';
import { Alert, Badge, Button, Card, ErrorState, LoadingState } from '../ui';

type Status = 'loading' | 'ready' | 'error';

/**
 * Whether the background is doing anything, and whether anything is waiting.
 *
 * The status panel beside this one says the broker is up, which means a message
 * can be queued. It says nothing about whether a worker is reading it — and an
 * upload whose worker is down looks exactly like an upload that is slow, from
 * every screen in the product (Phase 32).
 *
 * `null` is shown as "unknown" rather than as a zero, because a queue reported
 * empty because nobody could look is worse than one that says so.
 */
export function AdminBackground() {
  const [status, setStatus] = useState<Status>('loading');
  const [report, setReport] = useState<BackgroundReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(() => {
    setStatus('loading');
    getBackground()
      .then((data) => {
        setReport(data);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught : null);
        setStatus('error');
      });
  }, []);

  useEffect(load, [load]);

  const workers = report ? report.workers.count : null;
  const waiting = report ? Object.values(report.queues).reduce(sum, 0) : null;

  return (
    <Card
      title="Background work"
      description="Who is listening, what is waiting, and whether anything is late."
      actions={
        <Button size="sm" variant="ghost" onClick={load}>
          Refresh
        </Button>
      }
    >
      {status === 'loading' && <LoadingState label="Asking the workers…" />}

      {status === 'error' && (
        <ErrorState
          description={error?.message ?? 'The API is not answering.'}
          requestId={error?.requestId}
          onRetry={load}
        />
      )}

      {status === 'ready' && report && (
        <>
          {workers === 0 && (
            <Alert tone="danger" title="No worker answered">
              Uploads will be accepted and nothing will process them. Start a worker, or check
              whether the worker deployment is running.
            </Alert>
          )}

          {report.work.late_versions > 0 && (
            <Alert tone="warning" title="Work is waiting longer than it should">
              {report.work.late_versions} dataset version(s) have been waiting more than{' '}
              {report.work.late_after_minutes} minutes. The oldest has waited{' '}
              {report.work.oldest_pending_minutes} minutes.
            </Alert>
          )}

          <ul className="sc-checks">
            <li className="sc-checks__item">
              <span className="sc-checks__name">workers</span>
              <Badge
                tone={workers === null ? 'neutral' : workers > 0 ? 'success' : 'danger'}
                srPrefix="workers"
              >
                {workers === null ? 'Unknown' : `${workers} listening`}
              </Badge>
            </li>
            <li className="sc-checks__item">
              <span className="sc-checks__name">waiting in queues</span>
              <Badge tone={waiting === null ? 'neutral' : waiting > 0 ? 'warning' : 'success'}>
                {waiting === null ? 'Unknown' : `${waiting} message(s)`}
              </Badge>
            </li>
            {Object.entries(report.queues).map(([queue, depth]) => (
              <li key={queue} className="sc-checks__item">
                <span className="sc-checks__name">queue: {queue}</span>
                <Badge tone="neutral">{depth === null ? 'Unknown' : `${depth} waiting`}</Badge>
              </li>
            ))}
            <li className="sc-checks__item">
              <span className="sc-checks__name">datasets being processed</span>
              <Badge tone="neutral">{report.work.pending_versions}</Badge>
            </li>
          </ul>
        </>
      )}
    </Card>
  );
}

function sum(total: number | null, depth: number | null): number | null {
  if (total === null || depth === null) return null;
  return total + depth;
}
